import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useMemo,
  useState
} from 'react';
import { prepareProps } from '../../utils/propUtils';

export const FixedContentContainerProviderContext =
  createContext<FixedContentContainerProviderValue>({
    containerElement: null,
    notificationContainerElement: null
  });

export type FixedContentContainerProviderValue = {
  containerElement: HTMLElement | null;
  notificationContainerElement: HTMLElement | null;
};

export type FixedContentContainerProviderProps = PropsWithChildren<
  Record<never, never>
>;

/**
 * This component should wrap the root of your application. It will provide the necessary data for all components to render fixed content correctly.
 * Without this components with fixed content will not work correctly if one of the parent containers creates a new stacking context (opacity, transform...).
 */
export const FixedContentContainerProvider: FunctionComponent<FixedContentContainerProviderProps> =
  React.memo((props) => {
    const { children } = prepareProps({ props });
    const [containerElement, setContainerElement] =
      useState<HTMLElement | null>(null);
    const [notificationContainerElement, setNotificationContainerElement] =
      useState<HTMLElement | null>(null);

    const contextValue = useMemo<FixedContentContainerProviderValue>(
      () => ({
        containerElement,
        notificationContainerElement
      }),
      [containerElement, notificationContainerElement]
    );

    return (
      <>
        <FixedContentContainerProviderContext.Provider value={contextValue}>
          {contextValue.containerElement ? children : null}
        </FixedContentContainerProviderContext.Provider>
        <div
          ref={setContainerElement}
          data-testid="FixedContentContainerProvider.container"
        ></div>
        <div
          ref={setNotificationContainerElement}
          data-testid="FixedContentContainerProvider.notificationContainer"
        ></div>
      </>
    );
  });
